import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Trophy, X, Target } from '@phosphor-icons/react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const imgSrc = (u) => (u?.startsWith('http') ? u : `${BACKEND}${u}`);
const eur = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

const KEY = 'incentivo_promo_state';
const DEFAULT_MAX = 1;
const today = () => new Date().toISOString().slice(0, 10);
// Pequeno atraso para a página assentar antes de abrir o popup.
const SHOW_DELAY_MIN = 2500, SHOW_DELAY_MAX = 6000;
const rand = (a, b) => a + Math.random() * (b - a);

function readState() {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || '{}');
    if (s.date !== today()) return { date: today(), count: 0 };
    return { date: today(), count: s.count || 0 };
  } catch { return { date: today(), count: 0 }; }
}
function writeState(s) { try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* noop */ } }

export default function IncentivoPromoPopup() {
  const [current, setCurrent] = useState(null);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      // Definições do Admin: ligar/desligar + máximo por dia. Só a consultores.
      let cfg = { enabled: true, max_per_day: DEFAULT_MAX, is_consultor: false };
      try { cfg = (await api.get('/crm/incentivos/popup-config')).data || cfg; } catch { return; }
      if (!alive || !cfg.enabled || !cfg.is_consultor || cfg.max_per_day <= 0) return;
      if (readState().count >= cfg.max_per_day) return; // já apareceu hoje o nº permitido
      // Incentivos aplicáveis a este consultor, com imagem e ainda por alcançar.
      let elig = [];
      try {
        const r = await api.get('/crm/incentivos/ativas');
        if (!alive) return;
        elig = (r.data || []).filter((c) => (c.imagens_urls || []).length > 0 && !c.atingiu && c.falta > 0);
      } catch { return; }
      if (!elig.length) return;
      timerRef.current = setTimeout(() => {
        const st = readState();
        if (st.count >= cfg.max_per_day) return;
        const pick = elig[Math.floor(Math.random() * elig.length)];
        setCurrent(pick);
        writeState({ date: today(), count: st.count + 1 });
      }, rand(SHOW_DELAY_MIN, SHOW_DELAY_MAX));
    })();
    return () => { alive = false; clearTimeout(timerRef.current); };
  }, []);

  const close = () => setCurrent(null);
  const goToIncentivo = () => { close(); navigate('/incentivos'); };

  if (!current) return null;
  const pct = Math.min(100, current.percentagem || 0);
  const img = (current.imagens_urls || [])[0];

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="incentivo-promo-popup" onClick={close}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          {img && <img src={imgSrc(img)} alt={current.nome} className="w-full h-48 object-cover" data-testid="incentivo-promo-image" />}
          <button onClick={close} data-testid="incentivo-promo-close"
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white grid place-items-center hover:bg-black/70"><X size={18} /></button>
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-amber-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
            <Trophy size={13} weight="fill" /> Incentivo
          </div>
        </div>
        <div className="p-5">
          <h3 className="text-lg font-bold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>{current.nome}</h3>
          {current.premio && <p className="text-sm text-amber-700 font-medium mt-0.5">🎁 {current.premio}</p>}

          <div className="mt-4">
            <div className="flex items-end justify-between text-sm mb-1">
              <span className="text-[#6B7280]">Já conseguiu</span>
              <span className="font-bold text-[#0F1829]" data-testid="incentivo-promo-total">{eur(current.conseguido)}</span>
            </div>
            <div className="h-3 rounded-full bg-[#F1EEE6] overflow-hidden">
              <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between text-xs text-[#6B7280] mt-1">
              <span className="inline-flex items-center gap-1"><Target size={13} /> Objetivo {eur(current.valor_objetivo)}</span>
              <span>{pct}%</span>
            </div>
          </div>

          <div className="mt-4 rounded-xl bg-[#0F1829] text-white p-4 text-center">
            <p className="text-[11px] uppercase tracking-wide text-white/60">Falta apenas</p>
            <p className="text-2xl font-bold" data-testid="incentivo-promo-falta">{eur(current.falta)}</p>
          </div>

          <button onClick={goToIncentivo} data-testid="incentivo-promo-cta"
            className="mt-4 w-full py-2.5 rounded-lg bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors">
            Ver incentivo
          </button>
        </div>
      </div>
    </div>
  );
}
